import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Anchor, Button, Icon, Tooltip } from 'components/atoms';
import TabNavigation, { TabNavigationItem } from 'components/molecules/TabNavigation/TabNavigation';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { ReferenzType } from 'types';
import { copyQuery } from 'utils';
import { useTranslation, useUserTier } from 'utils/hooks';
import { useToggleFollowProfileMutation } from 'utils/hooks/mutations';
import { useUserQuery } from 'utils/hooks/queries';
import useDirektkauf from 'utils/hooks/state/useDirektkauf';

import css from './ProfileHeader.module.scss';

const Hint = dynamic(() => import('components/atoms/Hint/Hint'));
const ControlledHint = dynamic(() => import('components/atoms/Hint/ControlledHint'));

interface HeaderProps
{
  title: string | React.ReactNode;
  subTitle?: string;
  tabs?: TabNavigationItem[];
  id: string;
  type: ReferenzType;
}

const Header: React.FC<HeaderProps> = (props) =>
{
  const { title, subTitle, tabs = [], id, type } = props;
  const queryClient = useQueryClient();
  const { data: user } = useUserQuery();
  const router = useRouter();
  const { t, r } = useTranslation(['common', 'profiles', 'direktkauf']);
  const tier = useUserTier();
  const isDirektkaufPage = router.pathname.includes('plus') && tier === 'free';
  const [showDirektkauf, setShowDirektkauf] = useState(false);

  const [maxFollowIsOpen, setMaxFollowIsOpen] = useState(false);

  useEffect(() =>
  {
    const timeout = setTimeout(() =>
    {
      setShowDirektkauf(true);
    }, 1);
    return () => clearTimeout(timeout);
  }, []);


  const [, setDirektkauf] = useDirektkauf();

  const followMutation = useToggleFollowProfileMutation({
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user'] }),
  });

  const isSubscribed = user?.subscriptions.some((sub) => sub.ref.id === id) || false;

  const isStandardansicht = router.pathname.split('/').length === 4;
  const pathname = isStandardansicht
    ? router.pathname + '/verlauf'
    : router.pathname.substring(0, router.pathname.lastIndexOf('/'));
  const query = copyQuery(router.query, ['id', 'token']);

  return (
    <div id="profile-header" className={clsx(css.container, 'full-width-ignore-bounds')}>

      <div className={css.headingContainer}>
        <div className={css.heading}>
          <h1>
            {title}
            {![ReferenzType.Unternehmen, ReferenzType.AusländischesUnternehmen].includes(type) && (
              <sup>
                <a
                  className={css.disclaimerLink}
                  href="#disclaimer"
                  title={t('profiles:personen.disclaimerLink')}
                ><Icon name="info" /></a>
              </sup>
            )}
          </h1>
          {subTitle && <span>{subTitle}</span>}
        </div>

       {tier === 'free' && !isDirektkaufPage && (
          <Hint
            id="direktkauf"
            text={r('direktkauf:hint.text', { name: title })}
            align='end'
            delay={20}
            actions={

              <Button onClick={() => setDirektkauf('info')}>
                {t('direktkauf:hint.action')}
              </Button>
            }
          >
            <span className={css.freeActions}>
              {showDirektkauf && (<Tooltip
                content={<span>{t('direktkauf:tooltip')}</span>}
                side="left"
              >
                <Button
                  variant="secondary"
                  onClick={() => setDirektkauf('info')}
                >
                  <Icon name="unlock" />
                  {t('direktkauf:hint.unlock')}
                </Button>
              </Tooltip>)}
            </span>
          </Hint>
       )}

        {tier != 'free' && (
          <div className={css.actions}>
                {user?.hasMaxFollowedProfiles && !isSubscribed ? (
                  <>
                    <ControlledHint
                      title={t('profiles:hints.maxFollowTitle')}
                      text={tier === 'freemium' ? t('profiles:hints.maxFollowText') : t('profiles:hints.plusMaxFollowText')}
                      dismissText={t('profiles:hints.dismissMaxFollow')}
                      align='end'
                      isOpen={maxFollowIsOpen}
                      setIsOpen={setMaxFollowIsOpen}
                      actions={
                        tier === 'freemium' ? (
                          <Button onClick={() => router.push('/user')}>
                          {t('profiles:actions.switch')}
                        </Button> ) :
                          (
                            <Anchor as="button" asVariant="primary" href="mailto:kontakt@wirtschaft.at?subject=Anfrage - Enterprise Version von wirtschaft.at">
                              {t('profiles:actions.contact')}
                            </Anchor>
                          )
                      }
                    >
                  <span>
                      <Button
                        onClick={() => setMaxFollowIsOpen(true)}
                        variant={'primary'}
                      >
                        {t('profiles:actions.subscribe')}
                        <Icon name={'star'}/>
                      </Button>
                  </span>
                    </ControlledHint>
                  </>
                ) :
                  <>
                    <Hint
                      id="follow-profile"
                      text={t('profiles:hints.followProfile')}
                      align='end'
                      disabled={false}
                      actions={
                        <Button onClick={() => followMutation.mutate({ id, type, wantsToFollow: true })}>
                          {t('profiles:actions.subscribe')}
                        </Button>
                      }
                    >
                      <Tooltip
                        content={<div className={css.tooltip}>{t('profiles:hints.followProfile')}</div>}
                        side="bottom"
                      >
                  <span>
                      <Button
                        onClick={() => followMutation.mutate({ id, type, wantsToFollow: !isSubscribed })}
                        variant={isSubscribed ? 'transparent' : 'primary'}
                      >
                        {t(!isSubscribed ? 'profiles:actions.subscribe' : 'profiles:actions.unsubscribe')}
                        <Icon name={isSubscribed ? 'starFilled' : 'star'}/>
                      </Button>
                  </span>
                      </Tooltip>
                    </Hint>
                  </>
                }

            <nav className={css.ansicht}>
              <ul>

                {
                  isStandardansicht
                    ? <Tooltip content={t('profiles:actions.switchToStandardansicht')}><li><span className={css.selected}><div className={css.flexLink}><Icon name="profile" /></div></span></li></Tooltip>
                    : <li><Link href={{ pathname, query }}><div className={css.flexLink}>{t('profiles:actions.standardAnsicht')}<Icon name="profile" /></div></Link></li>
                }


                {
                  isStandardansicht
                    ? <li><Link href={{ pathname, query }}><div className={css.flexLink}>{t('profiles:actions.timelineAnsicht')}<Icon name="history" /></div></Link></li>
                    : <Tooltip content={t('profiles:actions.switchToZeitlicherVerlauf')}><li><span className={css.selected}><div className={css.flexLink}><Icon name="history" /></div></span></li></Tooltip>
                }

              </ul>
            </nav>

          </div>
        )}

      </div>

      {tabs.length > 0 && <TabNavigation items={tabs} />}

    </div>
  );
};

export default Header;
